<template>
    <div class="ls-billing-view--container">
        <div v-if="!loading">
            <div test-id="balance_row" class="ls-billing-view--balance-row">
                <div class="ls-billing-view--header">
                    <span class="ls-billing-view--account-balance">Account Balance: </span>
                    <span
                        :class="[
                            'ls-billing-view--balance-amount',
                            customer.balance >= 0 ? 'positive' : 'negative',
                        ]"
                        >{{ formatBalance(customer.balance) }}</span
                    >
                </div>
                <div class="ls-billing-view--balance-buttons">
                    <LsButton
                        track-id="billing_view-manually_credit"
                        text="Manually Credit"
                        type="button"
                        @click="manuallyCredit"
                    />
                    <LsButton
                        track-id="billing_view-balance_customer_account"
                        text="Balance Customer Account"
                        :loading="isUpdating"
                        type="button"
                        @click="balanceUserAccount"
                    />
                </div>
            </div>
            <LsBillingTable
                v-if="showTable"
                test-id="billing_wrapper"
                :customer-billing="customerBilling"
            />
        </div>
        <LsContentPlaceholder
            v-if="showPlaceholder"
            :message="placeholderData.message"
            :image="placeholderData.image"
        />
    </div>
</template>

<script>
import _ from 'lodash';
import { lsErrorService, lsFilters, lsModalService } from 'ls-vue-common';
import { lsHelpers } from '@/helpers';
import { CreditCustomerModal } from '@/modals';
import LsBillingTable from '@/components/LsBillingTable/LsBillingTable.vue';
import LsContentPlaceholder from '@/components/LsContentPlaceholder/LsContentPlaceholder.vue';
import Customer from '@/graphql/queries/Customer.gql';
import CustomerBilling from '@/graphql/queries/CustomerBilling.gql';
import BalanceUserAccount from '@/graphql/mutations/BalanceUserAccount.gql';
import { LsButton } from '@/components/common';

export default {
    name: 'BillingView',
    components: {
        LsBillingTable,
        LsButton,
        LsContentPlaceholder,
    },
    data() {
        return {
            loading: 0,
            isUpdating: false,
        };
    },
    computed: {
        showPlaceholder() {
            return !_.size(this.customerBilling) || this.loading;
        },
        showTable() {
            return _.size(this.customerBilling) && !this.loading;
        },
        placeholderData() {
            if (this.loading) {
                return {
                    message: 'Loading...',
                    image: 'spinner',
                };
            }
            return {
                message: 'There are no billing items for this customer',
                image: 'inactive',
            };
        },
    },
    methods: {
        formatBalance(balance) {
            return lsFilters.currency({
                amount: balance,
                trimTrailingZeros: true,
            });
        },
        async balanceUserAccount() {
            this.isUpdating = true;
            this.$apollo
                .mutate({
                    mutation: BalanceUserAccount,
                    variables: {
                        user_id: _.toNumber(this.customer.user.id),
                    },
                    refetchQueries: [
                        {
                            query: Customer,
                            variables: {
                                id: _.toNumber(this.$route.params.customer_id),
                            },
                        },
                    ],
                })
                .catch((error) => {
                    lsErrorService.log({ error, showAlert: true });
                })
                .finally(() => {
                    this.isUpdating = false;
                });
        },
        manuallyCredit() {
            lsModalService.open({
                parent: this,
                component: CreditCustomerModal,
                props: {
                    title: `Credit Customer - ${this.customer.user.first_name} ${this.customer.user.last_name}`,
                },
            });
        },
    },
    apollo: {
        customer: {
            query: Customer,
            variables() {
                return {
                    id: _.toNumber(this.$route.params.customer_id),
                };
            },
            result({ data, loading }) {
                if (!loading) {
                    this.customer = data.customer;
                }
            },
            error(error) {
                lsErrorService.log({ error, showAlert: true });
                this.$apollo.queries.customer.skip = true;
                lsHelpers.goToParentView({ replace: true });
            },
            loadingKey: 'loading',
        },
        customerBilling: {
            query: CustomerBilling,
            variables() {
                return {
                    customer_id: _.toNumber(this.$route.params.customer_id),
                };
            },
            result({ data, loading }) {
                if (!loading) {
                    this.customerBilling = data.customerBilling;
                }
            },
            error(error) {
                lsErrorService.log({ error, showAlert: true });
                this.$apollo.queries.customerBilling.skip = true;
                lsHelpers.goToParentView({ replace: true });
            },
            loadingKey: 'loading',
        },
    },
};
</script>

<style lang="scss" scoped>
.ls-billing-view--container {
    padding: 10px 20px 20px 20px;
    background-color: $white;
    box-shadow: $box-shadow;
    border-radius: $border-radius;

    .ls-content-placeholder--container {
        background-color: $white;
        position: inherit;
        box-shadow: none;
    }
}

.ls-billing-view--balance-row {
    padding-top: 20px;
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.ls-billing-view--balance-buttons {
    display: flex;
    justify-content: space-between;

    button:first-child {
        margin-right: 8px;
    }
}

.ls-billing-view--account-balance {
    font-size: $size-2;
    color: $primary;
    font-weight: $bold;
}

.ls-billing-view--balance-amount {
    font-size: $size-2;

    &.positive {
        color: $primary;
    }

    &.negative {
        color: $danger;
    }
}
</style>
